/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import type { AnalyticsEvent } from "@packages/analytics";
import { useAnalytics } from "@packages/analytics";
import { Link } from "@packages/framework";
import { usePathMatch } from "@packages/sdk";
import { type ComponentPropsWithoutRef, type ReactNode } from "react";

import { radius, spacing } from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { ButtonProps } from "../Button/Button.emotion";
import { Button } from "../Button/Button.emotion";
import { IconButton } from "../IconButton/IconButton.emotion";
import type { SidebarProps } from "../Sidebar/Sidebar.emotion";
import { SidebarTabCollapsible } from "./SidebarTabCollapsible.emotion";

const activeStyle: Interpolation = {
  opacity: 1,
};
const buttonStyle: Interpolation = {
  ":active": { backgroundColor: "transparent !important" },
  ":focus-visible": { backgroundColor: "transparent !important" },
  ":hover": { backgroundColor: "transparent !important" },
  backgroundColor: "transparent !important",
  gap: spacing.ms,
  justifyContent: "flex-start",
};
const inactiveStyle: Interpolation = {
  ":active": { opacity: 1 },
  ":focus-visible": { opacity: 1 },
  ":hover": { opacity: 1 },
  opacity: 0.5,
};
const iconButtonStyle: Interpolation = {
  ":active": { backgroundColor: "transparent !important" },
  ":focus-visible": { backgroundColor: "transparent !important" },
  ":hover": { backgroundColor: "transparent !important" },
  backgroundColor: "transparent !important",
  borderRadius: radius.m,
};
const linkStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  whiteSpace: "normal",
};
const nestedStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  paddingLeft: spacing.xl,
};
const spanStyle: Interpolation = {
  [cssMediaQueryMinWidthTablet]: { flex: 1 },
  flex: 0,
  minWidth: "fit-content",
};
const tabHasSubTabsStyle: Interpolation = {
  [cssMediaQueryMinWidthTablet]: { display: "flex" },
  display: "none",
};

type BaseSidebarTabProps = ComponentPropsWithoutRef<typeof Link> & {
  actionRight?: ReactNode;
  analyticsEvent?: AnalyticsEvent;
  children?: ReactNode;
  message: string;
  state?: SidebarProps["state"];
  hidden?: boolean;
  subTabs?: SidebarTabProps[];
};

export type SidebarTabProps = BaseSidebarTabProps &
  (
    | { state: "closed"; icon: ButtonProps["startIcon"] }
    | { state?: "expanded"; icon?: ButtonProps["startIcon"] }
  );

export const SidebarTab = ({
  actionRight,
  analyticsEvent,
  children,
  href,
  icon,
  message,
  state = "expanded",
  hidden = false,
  subTabs = [],
  ...props
}: SidebarTabProps) => {
  const matcher = usePathMatch();
  const analytics = useAnalytics();
  const isSidebarTabActive = matcher(href?.toString() ?? "");

  if (hidden) return null;

  if (state === "closed")
    return (
      <IconButton
        asChild
        icon={icon}
        css={[
          iconButtonStyle,
          isSidebarTabActive ? activeStyle : inactiveStyle,
        ]}
        variant="transparentPrimary"
      >
        <Link href={href} {...props} />
      </IconButton>
    );

  return (
    <>
      <Button
        asChild
        isFullWidth
        startIcon={icon}
        css={[
          buttonStyle,
          isSidebarTabActive ? activeStyle : inactiveStyle,
          linkStyle,
          subTabs.length > 0 && tabHasSubTabsStyle,
        ]}
        variant="transparentPrimary"
      >
        <Link
          href={href}
          {...props}
          onClick={() => analyticsEvent && analytics.track(analyticsEvent)}
        >
          <span css={spanStyle}>
            <I18nMessage message={message} />
          </span>
          {actionRight}
        </Link>
      </Button>
      {children && <div css={nestedStyle}>{children}</div>}
      {subTabs.length > 0 && (
        <SidebarTabCollapsible
          href={href}
          icon={icon}
          message={message}
          state={state}
          subTabs={subTabs}
          {...props}
        />
      )}
    </>
  );
};

SidebarTab.displayName = "SidebarTab";
